const ROLE_ALIASES = {
  emp: "emp",
  employee: "emp",
  recruiter: "recruiter",
  hr: "hr",
  hr_manager: "hr_manager",
  "hr manager": "hr_manager",
  admin_user: "admin_user",
  "admin user": "admin_user",
  business_user: "business_user",
  "business user": "business_user",
  super_admin: "super_admin",
  "super admin": "super_admin",
  admin: "super_admin",
  developer_head: "developer_head",
  "developer head": "developer_head",
  developer: "developer",
};

const ROLE_PERMISSIONS = {
  emp: new Set([
    "reimbursement.view", "reimbursement.create",
    "reimbursement.dashboard.view", "reimbursement.approved.view",
  ]),
  recruiter: new Set([
    "candidate.view",
    "candidate.create",
    "candidate.edit",
    "interview.manage",
    "offer.create",
    "offer.view",
    "offer.manage",
    "headcount.view",
    "job.manage",
    "email.send",
    "stage1.manage",
    "performance.view",
  ]),
  hr: new Set([
    "candidate.view",
    "candidate.create",
    "candidate.edit",
    "candidate.delete",
    "interview.manage",
    "job.edit",
    "job.manage",
    "offer.view",
    "offer.manage",
    "offer.documents",
    "offer.viewGlobal",
    "offer.directEdit",
    "email.send",
    "stage1.manage",
    "performance.view",
  ]),
  hr_manager: new Set([
    "candidate.view",
    "candidate.create",
    "candidate.edit",
    "candidate.delete",
    "interview.manage",
    "job.edit",
    "job.manage",
    "offer.view",
    "offer.manage",
    "offer.documents",
    "offer.viewGlobal",
    "offer.directEdit",
    "offer.master",
    "workflow.master",
    "email.send",
    "stage1.manage",
    "job.export",
  ]),
  admin_user: new Set([
    "candidate.view",
    "candidate.create",
    "candidate.edit",
    "candidate.delete",
    "interview.manage",
    "job.edit",
    "job.manage",
    "offer.view",
    "offer.manage",
    "offer.documents",
    "offer.viewGlobal",
    "offer.directEdit",
    "offer.master",
    "workflow.master",
    "email.send",
    "stage1.manage",
    "headcount.view",
    "headcount.sync",
    "headcount.export",
    "headcount.import",
    "employee.view",
    "employee.edit",
    "trainee.view",
    "trainee.create",
    "trainee.edit",
    "trainee.delete",
    "trainee.export",
    "intern.view",
    "intern.create",
    "intern.edit",
    "intern.delete",
    "intern.export",
    "job.export",
    "payroll.view",
    "payroll.edit",
    "payroll.export",
    "variable.view",
    "variable.edit",
    "variable.export",
    "resource_cost.view",
    "resource_cost.edit",
    "resource_cost.export",
    "project.view",
    "project.create",
    "project.edit",
    "project.delete",
    "customer.view",
    "customer.create",
    "customer.edit",
    "customer.delete",
    "sales.view",
    "sales.create",
    "sales.edit",
    "sales.delete",
    "sales.import",
  ]),
  business_user: new Set([
    "candidate.view",
    "candidate.create",
    "candidate.edit",
    "candidate.delete",
    "interview.manage",
    "job.edit",
    "job.manage",
    "offer.view",
    "offer.manage",
    "headcount.view",
    "employee.view",
    "trainee.view",
    "intern.view",
    "payroll.view",
    "variable.view",
    "resource_cost.view",
    "project.view",
    "customer.view",
    "sales.view",
    "job.export",
  ]),
  super_admin: new Set([
    "*",
    "candidate.view",
    "candidate.create",
    "candidate.edit",
    "candidate.delete",
    "interview.manage",
    "job.edit",
    "job.manage",
    "offer.view",
    "offer.manage",
    "headcount.view",
    "headcount.sync",
    "employee.view",
    "employee.create",
    "employee.edit",
    "employee.delete",
    "trainee.view",
    "trainee.create",
    "trainee.edit",
    "trainee.delete",
    "intern.view",
    "intern.create",
    "intern.edit",
    "intern.delete",
    "payroll.view",
    "payroll.edit",
    "variable.view",
    "variable.edit",
    "resource_cost.view",
    "resource_cost.edit",
    "project.view",
    "project.create",
    "project.edit",
    "project.delete",
    "customer.view",
    "customer.create",
    "customer.edit",
    "customer.delete",
    "sales.view",
    "sales.create",
    "sales.edit",
    "sales.delete",

  ]),
  developer_head: new Set([
    "*",
    "support_ticket.view",
    "support_ticket.create",
    "support_ticket.assign",
  ]),
  developer: new Set([
    "*",
    "support_ticket.view",
    "support_ticket.create",
    "support_ticket.close",
    "performance.view",
  ]),
};
    
export function normalizeRole(role) {
  const key = String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  return ROLE_ALIASES[key] || "unknown";
}

export function hasRole(role, allowedRoles = []) {
  const normalizedRole = normalizeRole(role);
  return allowedRoles.map(normalizeRole).includes(normalizedRole);
}

// PRIVILEGED TIER: super_admin, Developer Head and Developer are granted every
// page/action (mirrors the backend isFullAccessRole). Frontend guards defer to
// this so the two developer roles behave exactly like super_admin across the
// portal. The support-ticket workflow still enforces its own stricter rules
// (assign = Developer Head only; close = the assigned developer only).
export function isPrivilegedRole(role) {
  const normalizedRole = normalizeRole(role);
  return normalizedRole === "super_admin" || normalizedRole === "developer_head" || normalizedRole === "developer";
}

export function hasPermission(role, permission) {
  const permissions = ROLE_PERMISSIONS[normalizeRole(role)] || new Set();
  return permissions.has("*") || permissions.has(permission);
}

export function isHeadcountHomeRole(role) {
  return hasRole(role, ["super_admin", "admin_user", "business_user"]);
}

// Analytics previously lived under Admin Team and was the landing page for the
// privileged tier. It now lives under Sudo Access (/admin/analytics, outside
// the portal sidebar), so there is no Admin Team landing page anymore. Kept
// (always false) so existing callers keep working without changes.
export function isAdminTeamHomeRole(role) {
  void role;
  return false;
}

// Landing page per role. Super Admin falls through to the headcount landing
// via isHeadcountHomeRole; everyone else keeps their existing destination.
export function getHomePathForRole(role) {
  if (isAdminTeamHomeRole(role)) return "/headcount";
  return isHeadcountHomeRole(role) ? "/headcount" : "/dashboard";
}
